import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle2, Minus, Sparkles, Loader2, Target } from "lucide-react";
import { invokeFunction } from "@/integrations/supabase/client";
import type { HubState, LearnerProfile, Taxonomy, GeneratedTaxonomy, IdentifiedGap } from "@/lib/learnerHub/types";
import { buildGapGrid } from "@/lib/learnerHub/utils";
import { ROLE_PROFILES, unitById } from "@/lib/learnerHub/constants";

interface Props {
  state: HubState;
  setHub: (partial: Partial<HubState>) => void;
  taxonomyLibrary: Taxonomy[];
  onGenerateCourse?: (learner: LearnerProfile, gapUnitIds: string[], targetRole: string) => void;
  onGapsComputed?: (gaps: IdentifiedGap[]) => void;
}

interface AnalysisModal {
  learner: LearnerProfile;
  gapUnitIds: string[];
  targetRole: string;
}

function confidenceColor(confidence: string | null): string {
  switch (confidence) {
    case "HIGH": return "text-green-600";
    case "MEDIUM": return "text-amber-500";
    case "LOW": return "text-red-400";
    default: return "";
  }
}

export default function GapAnalysisTab({ state, setHub, taxonomyLibrary, onGenerateCourse, onGapsComputed }: Props) {
  const { learners, selectedRole, taxonomy } = state;
  const roles = Object.keys(ROLE_PROFILES);
  const requiredUnitIds = ROLE_PROFILES[selectedRole] || [];
  const grid = buildGapGrid(learners, selectedRole);
  const isGenerated = "generated" in taxonomy && (taxonomy as GeneratedTaxonomy).generated;

  // Emit aggregate gap counts upward for demo rail step 4
  useEffect(() => {
    if (!onGapsComputed) return;
    const gaps: IdentifiedGap[] = requiredUnitIds
      .map((uid) => ({
        unitId: uid,
        label: unitById(uid)?.label || uid,
        missingCount: grid.filter((row) => !row.coverage.find((c) => c.unitId === uid)?.covered).length,
      }))
      .filter((g) => g.missingCount > 0);
    onGapsComputed(gaps);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole, learners.length]);

  const [analysisModal, setAnalysisModal] = useState<AnalysisModal | null>(null);
  const [analysisText, setAnalysisText] = useState<string | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const openAnalysis = async (learner: LearnerProfile, gapUnitIds: string[], targetRole: string) => {
    setAnalysisModal({ learner, gapUnitIds, targetRole });
    setAnalysisText(null);
    setAnalysisLoading(true);

    const gapDescriptions = gapUnitIds
      .map((uid) => {
        const unit = unitById(uid);
        return `- ${uid}: ${unit?.label ?? uid}`;
      })
      .join("\n");

    const prompt = `You are an expert learning designer. Analyze the following competency gaps for a learner.

Learner: ${learner.name}
Current role: ${learner.role}
Target role: ${targetRole}

Missing competencies:
${gapDescriptions}

Provide a concise analysis (3–4 short paragraphs) covering:
1. What these gaps mean for their development path toward ${targetRole}
2. The most critical skills to address first and why
3. How a focused course should be structured to close these gaps

Be specific and actionable. Keep it under 300 words.`;

    try {
      const { data, error } = await invokeFunction("course-creation-chat", {
        body: {
          messages: [{ role: "user", content: prompt }],
          suggestionMode: false,
        },
      });
      if (error) throw new Error((error as { message?: string }).message || "Analysis failed");
      setAnalysisText(data?.message || "Analysis complete.");
    } catch {
      setAnalysisText("Unable to generate analysis. You can still proceed to generate the course.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Taxonomy selector */}
      {taxonomyLibrary.length > 1 && (
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm font-medium whitespace-nowrap">Taxonomy:</label>
          <select
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
            value={taxonomy.customerId}
            onChange={(e) => {
              const t = taxonomyLibrary.find((x) => x.customerId === e.target.value);
              if (t) setHub({ taxonomy: t });
            }}
          >
            {taxonomyLibrary.map((t) => (
              <option key={t.customerId} value={t.customerId}>
                {t.name}{"generated" in t && (t as GeneratedTaxonomy).generated ? " (AI)" : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {isGenerated && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Gap analysis uses CCM-Eleva role profiles. Switch to the CCM-Eleva taxonomy to see
          learner coverage data, or define role profiles for this taxonomy.
        </p>
      )}

      {/* Role selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm font-medium whitespace-nowrap">Role profile:</label>
        <select
          className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
          value={selectedRole}
          onChange={(e) => setHub({ selectedRole: e.target.value })}
        >
          {roles.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* Required units */}
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          Required units for {selectedRole}
        </p>
        <div className="flex flex-wrap gap-2">
          {requiredUnitIds.map((uid) => {
            const unit = unitById(uid);
            return (
              <Badge key={uid} variant="outline" className="text-xs">
                <span className="font-mono mr-1">{uid}</span>
                {unit?.label}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* Coverage grid */}
      {learners.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No profiles loaded. Import learners first.
        </p>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left px-4 py-3 font-medium sticky left-0 bg-muted/40 z-10 min-w-[160px]">
                    Learner
                  </th>
                  <th className="text-center px-3 py-3 font-medium min-w-[72px] whitespace-nowrap">
                    Coverage
                  </th>
                  {requiredUnitIds.map((uid) => (
                    <th
                      key={uid}
                      className="text-center px-3 py-3 font-mono text-xs min-w-[80px]"
                      title={unitById(uid)?.label}
                    >
                      {uid}
                    </th>
                  ))}
                  {onGenerateCourse && (
                    <th className="text-center px-3 py-3 font-medium min-w-[110px]" />
                  )}
                </tr>
              </thead>
              <tbody>
                {grid.map(({ learner, coverage }) => {
                  const coveredCount = coverage.filter((c) => c.covered).length;
                  const gapUnitIds = coverage.filter((c) => !c.covered).map((c) => c.unitId);
                  const hasGaps = gapUnitIds.length > 0;
                  return (
                    <tr key={learner.id} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-2.5 sticky left-0 bg-background z-10">
                        <p className="font-medium text-sm">{learner.name}</p>
                        <p className="text-xs text-muted-foreground">{learner.role}</p>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span
                          className={`text-xs font-semibold ${
                            coveredCount === requiredUnitIds.length
                              ? "text-green-600"
                              : coveredCount === 0
                              ? "text-red-500"
                              : "text-amber-600"
                          }`}
                        >
                          {coveredCount}/{requiredUnitIds.length}
                        </span>
                      </td>
                      {coverage.map((cell) => (
                        <td key={cell.unitId} className="px-3 py-2.5 text-center">
                          {cell.covered ? (
                            <CheckCircle2
                              className={`h-4 w-4 mx-auto ${confidenceColor(cell.confidence)}`}
                            />
                          ) : (
                            <Minus className="h-4 w-4 mx-auto text-muted-foreground/30" />
                          )}
                        </td>
                      ))}
                      {onGenerateCourse && (
                        <td className="px-3 py-2.5 text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 gap-1 text-xs whitespace-nowrap"
                            disabled={!hasGaps}
                            onClick={() => openAnalysis(learner, gapUnitIds, selectedRole)}
                            title={hasGaps ? `Analyze ${gapUnitIds.length} gap${gapUnitIds.length !== 1 ? "s" : ""}` : "No gaps for this role"}
                          >
                            <Sparkles className="h-3 w-3" />
                            {hasGaps ? `${gapUnitIds.length} gap${gapUnitIds.length !== 1 ? "s" : ""}` : "Complete"}
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <p className="text-xs text-muted-foreground flex items-center gap-4 flex-wrap">
        <span className="flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> HIGH confidence
        </span>
        <span className="flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5 text-amber-500" /> MEDIUM confidence
        </span>
        <span className="flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5 text-red-400" /> LOW confidence
        </span>
        <span className="flex items-center gap-1">
          <Minus className="h-3.5 w-3.5 text-muted-foreground/40" /> Gap
        </span>
      </p>

      {/* Gap analysis modal */}
      <Dialog open={!!analysisModal} onOpenChange={(open) => { if (!open) setAnalysisModal(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Gap Analysis — {analysisModal?.learner.name}
            </DialogTitle>
          </DialogHeader>

          {/* Gap unit badges */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Missing units for {analysisModal?.targetRole}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {analysisModal?.gapUnitIds.map((uid) => {
                const unit = unitById(uid);
                return (
                  <Badge key={uid} variant="outline" className="text-xs">
                    <span className="font-mono mr-1">{uid}</span>
                    {unit?.label}
                  </Badge>
                );
              })}
            </div>
          </div>

          {/* AI analysis */}
          <div className="rounded-lg border bg-muted/30 p-4 min-h-[120px]">
            {analysisLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyzing gaps…
              </div>
            ) : (
              <ScrollArea className="max-h-64">
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{analysisText}</p>
              </ScrollArea>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setAnalysisModal(null)}>
              Cancel
            </Button>
            <Button
              className="flex-1 gap-2"
              disabled={analysisLoading}
              onClick={() => {
                if (onGenerateCourse && analysisModal) {
                  onGenerateCourse(analysisModal.learner, analysisModal.gapUnitIds, analysisModal.targetRole);
                }
                setAnalysisModal(null);
              }}
            >
              <Sparkles className="h-4 w-4" />
              Generate course
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
